import { useEffect, useState } from "react";
import {
	Button,
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from ".";
import { HiChevronDown, HiOutlinePlusCircle } from "react-icons/hi";

import { cn } from "../../lib/utils";

export interface SelectPillsProps {
	title?: string;
	options: {
		value: string;
		label: string;
	}[];
	selectedValues: string[];
	onSubmit: (values: string[]) => void;
}

function SelectPills({
	title,
	options,
	selectedValues,
	onSubmit,
}: SelectPillsProps) {
	const [open, setOpen] = useState(false);
	const [selected, setSelected] = useState<string[]>(selectedValues || []);
	const isApplied = selectedValues.length > 0;

	useEffect(() => {
		setSelected(selectedValues);
	}, [open, selectedValues]);

	const onApplyClick = () => {
		onSubmit(selected);
		setOpen(false);
	};

	const onSelect = (e: Event, value: string) => {
		e.preventDefault();
		if (selected.includes(value)) {
			setSelected(selected.filter((v) => v !== value));
			return;
		}
		setSelected([...selected, value]);
	};

	return (
		<DropdownMenu open={open} onOpenChange={setOpen}>
			<DropdownMenuTrigger
				className={cn(
					"opacity-100 p-1 rounded-3xl border border-ui-border-input flex flex-row items-center cursor-pointer hover:bg-gray-50",
					{ "border-dashed pr-2": !isApplied },
				)}
			>
				<HiOutlinePlusCircle
					className={cn(
						"size-4 text-ui-text-2 transition-transform duration-75 rotate-0 mr-1",
						{ "rotate-45": isApplied },
					)}
				/>
				<p className="font-medium text-xs leading-4 text-ui-text-2">
					{title}
					{isApplied && (
						<>
							<span className="text-ui-border-input"> | </span>
							<span className="text-alta-light-green">
								{selectedValues
									.map(
										(value) =>
											options.find((option) => option.value === value)?.label,
									)
									.join(", ")}
							</span>
						</>
					)}
				</p>
				{isApplied && (
					<HiChevronDown className="text-alta-light-green size-3" />
				)}
			</DropdownMenuTrigger>
			<DropdownMenuContent align="start" className="p-0">
				<p className="mx-4 my-3 font-semibold text-xs leading-4 text-gray-900">
					{title}
				</p>
				<DropdownMenuSeparator />
				<div className="px-2">
					{options.map(({ value, label }) => (
						<DropdownMenuCheckboxItem
							key={value}
							className="text-xs leading-4 text-alta-black"
							checkboxClassName="size-3"
							onSelect={(e) => onSelect(e, value)}
							checked={selected.includes(value)}
						>
							{label}
						</DropdownMenuCheckboxItem>
					))}
				</div>
				<div className="w-full p-2">
					<Button
						className="w-full shadow-md font-medium text-xs leading-4"
						size="xs"
						onClick={onApplyClick}
					>
						Apply
					</Button>
				</div>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

export { SelectPills };
